<template>
  <div
    data-component-group="business-users-form"
    data-component="invite-member"
  >
    <slot name="beforeForm" />

    <form @:submit.prevent="onSubmit">
      <div class="inner-title">
        {{ $t('new_member') }}
      </div>

      <magic-container class="mt-4">
        <v-text-field
          v-model="firstName"
          :label="$t('first_name')"
          :error-messages="firstNameErrors"
          autofocus
          class="invite-member-first-name"
          @blur="v$.firstName.$touch()"
        />

        <v-text-field
          v-model="lastName"
          :label="$t('last_name')"
          :error-messages="lastNameErrors"
          class="invite-member-last-name"
          @blur="v$.lastName.$touch()"
        />
      </magic-container>

      <v-text-field
        v-model="email"
        :label="$t('email')"
        prepend-inner-icon="email"
        :error-messages="emailErrors"
        class="invite-member-email"
        @blur="v$.email.$touch()"
      />

      <v-radio-group
        v-model="role"
        class="radio-group-role"
        :error-messages="roleErrors"
        required
      >
        <span>
          Role
          <Tooltip
            :text="$t('business_members_invite_role_tooltip')"
            :text-two="$t('business_members_invite_role_two_tooltip')"
            :text-three="$t('business_members_invite_role_three_tooltip')"
            :delay="3000"
          />
        </span>

        <div class="radio-items-wrapper">
          <v-radio
            v-for="item in roles"
            :key="item.name"
            :label="item.name"
            :value="item.code"
            class="radio-item"
            color="primary"
          />
        </div>
      </v-radio-group>

      <div class="form-actions">
        <v-btn v-if="onCancel" class="cancel-button" variant="text" @click="cancel">
          {{ cancelTriggerLabel }}
        </v-btn>

        <v-btn
          v-if="onSubmit"
          color="primary"
          variant="flat"
          :loading="isLoading"
          @click="submit"
        >
          {{ confirmTriggerLabel }}
        </v-btn>
      </div>
    </form>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required, email } from '@vuelidate/validators';
  import Tooltip from '../../tooltips/Tooltip';
  import { inject } from 'vue';

  export default {
    components: {
      Tooltip,
    },
    setup () {
      return {
        v$: useVuelidate()
      }
    },
    validations() {
      return {
        firstName: {
          required,
        },
        lastName: {
          required,
        },
        email: {
          required,
          email,
        },
        role: {
          required,
        },
      };
    },
    props: {
      isOn: {
        type: Boolean,
        required: true,
      },
      isLoading: {
        type: Boolean,
        required: true,
      },
      onCancel: {
        type: Function,
        default: undefined,
      },
      onSubmit: {
        type: Function,
        default: undefined,
      },
      cancelTriggerLabel: {
        type: String,
        default() {
          // return this.$t('cancel');
          return inject('i18n').t('cancel');
        },
      },
      confirmTriggerLabel: {
        type: String,
        default() {
          // return this.$t('send_invite');
          return inject('i18n').t('send_invite');
        },
      },
    },
    data() {
      return {
        firstName: '',
        lastName: '',
        email: '',
        role: null,
        tooltipLink: import.meta.env.VUE_APP_HELP_ADDING_MEMBER_URL,
      };
    },
    computed: {
      roles() {
        return this.$store.getters['users/subRoles'];
      },
      firstNameErrors() {
        return this.v$.firstName.$errors.map((error) => {
          if (error.$validator === 'required') {
            return this.$t('first_name_required');
          }
          return error.$message;
        });
      },
      lastNameErrors() {
        return this.v$.lastName.$errors.map((error) => {
          if (error.$validator === 'required') {
            return this.$t('last_name_required');
          }
          return error.$message;
        });
      },
      emailErrors() {
        return this.v$.email.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('email_is_required');
          }
          if (error.$validator === 'email') {
            return this.$t('must_be_valid_email');
          }
          return error.$message;
        });
      },
      roleErrors() {
        return this.v$.role.$errors.map(error => {
          if (error.$validator === 'required') {
            return this.$t('role_is_required');
          }
          return error.$message;
        });
      },
    },
    watch: {
      isOn(value) {
        if (!value) {
          this.clear();
        }
      },
    },
    methods: {
      inviteMember() {
        const { firstName, lastName, email, role } = this;

        const user = {
          firstname: firstName,
          lastname: lastName,
          email,
          role,
        };

        return this.onSubmit(user);
      },
      canSubmitRequest() {
        const { firstName, lastName, email } = this;

        return firstName && lastName && email;
      },
      submit() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.inviteMember();
      },
      clear() {
        this.v$.$reset();
        this.firstName = '';
        this.lastName = '';
        this.email = '';
        this.role = null;
      },
      cancel() {
        this.clear();
        this.onCancel();
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='business-users-form'][data-component='invite-member'] {
  background-color: rgb(var(--v-theme-surface));
  padding: $spacing-xl;

  .inner-title {
    font-weight: 700;
    color: #424242;

    .v-theme--dark & {
      color: rgb(var(--v-theme-on-surface));
    }
  }

  .container {
    padding: 0;
  }

// TODO replace with form-buttons
  .form-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .v-btn {
      margin-bottom: 0;

      &.trigger {
        .v-icon {
          margin: 0 6px 0 0;
        }
      }

      &:last-child {
        margin-left: 12px;
        margin-right: 0;
      }
    }
  }

  .radio-group-role {
    font-weight: 700;

    .radio-items-wrapper {
      margin-top: 12px;
      display: flex;
      align-items: center;


    }
  }
}
</style>
